import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/ui/components/badge';
import { Button } from '@/shared/ui/components/button';
import { apiErrorMessage } from '@/shared/api-client';
import { useIssuePosPin, usePosPins, type PosPinSlot } from './api';

interface Revealed {
  venueName: string;
  pin: string;
}

/**
 * The User's Venue PosPins (ADR 0074): one row per Venue where they hold `pos.kiosk.login`,
 * Not issued or Issued. Issue and Regenerate reveal the new PIN once.
 */
export function UserPosPins({ userId }: { userId: string }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = usePosPins(userId);
  const issue = useIssuePosPin(userId);
  const [confirmVenueId, setConfirmVenueId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runIssue(slot: PosPinSlot) {
    setError(null);
    setConfirmVenueId(null);
    try {
      const result = await issue.mutateAsync(slot.venueId);
      setRevealed({ venueName: slot.venueName, pin: result.posPin });
    } catch (caught) {
      setError(apiErrorMessage(t, 'users', caught));
    }
  }

  const slots = data?.items ?? [];

  return (
    <section aria-labelledby="user-pos-pins-title" className="mt-2 grid gap-3 border-t pt-4">
      <p id="user-pos-pins-title" className="text-sm font-medium">
        {t('users.venuePosPins.title')}
      </p>

      {isLoading && (
        <p className="text-muted-foreground text-sm">{t('users.venuePosPins.loading')}</p>
      )}

      {!isLoading && slots.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('users.venuePosPins.empty')}</p>
      )}

      {slots.length > 0 && (
        <ul className="grid gap-2">
          {slots.map((slot) => (
            <li key={slot.venueId} className="grid gap-2 rounded-md border p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{slot.venueName}</span>
                  <Badge variant={slot.status === 'issued' ? 'default' : 'secondary'}>
                    {t(`users.venuePosPins.status.${slot.status}`)}
                  </Badge>
                </div>
                {slot.status === 'issued' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={issue.isPending}
                    aria-label={t('users.venuePosPins.regenerateAt', { venue: slot.venueName })}
                    onClick={() => setConfirmVenueId(slot.venueId)}
                  >
                    {t('users.venuePosPins.regenerate')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={issue.isPending}
                    aria-label={t('users.venuePosPins.issueAt', { venue: slot.venueName })}
                    onClick={() => void runIssue(slot)}
                  >
                    {t('users.venuePosPins.issue')}
                  </Button>
                )}
              </div>
              {confirmVenueId === slot.venueId && (
                <div className="grid gap-2 rounded-md border border-destructive/40 p-2">
                  <p className="text-sm">
                    {t('users.venuePosPins.confirmRegenerate', { venue: slot.venueName })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={issue.isPending}
                      onClick={() => void runIssue(slot)}
                    >
                      {t('users.venuePosPins.regenerate')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setConfirmVenueId(null)}>
                      {t('actions.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {revealed && (
        <PosPinPanel
          title={t('users.venuePosPins.revealTitle', { venue: revealed.venueName })}
          pin={revealed.pin}
          onDismiss={() => setRevealed(null)}
        />
      )}
    </section>
  );
}

/** A freshly generated PosPin, shown once with a copy action. */
function PosPinPanel({
  title,
  pin,
  onDismiss,
}: {
  title: string;
  pin: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 dark:bg-amber-950/20">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-muted-foreground text-sm">{t('users.posPin.warning')}</p>
      <code
        data-testid="pos-pin"
        className="bg-background rounded border px-2 py-1 font-mono text-sm break-all"
      >
        {pin}
      </code>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => void copy()}>
          {copied ? t('users.posPin.copied') : t('users.posPin.copy')}
        </Button>
        <Button variant="outline" onClick={onDismiss}>
          {t('users.posPin.dismiss')}
        </Button>
      </div>
    </div>
  );
}
